import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import {
  REPORT_NAV_CATEGORIES,
  getReportById,
  getReportsByCategory,
  getReportNavCategoriesVisible,
} from '../config/reportRegistry';
import ClientStatusQuickEdit from './ClientStatusQuickEdit';
import ClientsReport from './ClientsReport';
import CaseloadToolsReport from './CaseloadToolsReport';
import TpcAccessVsCornerstoneReport from './TpcAccessVsCornerstoneReport';
import ExitAccessVsCornerstoneReport from './ExitAccessVsCornerstoneReport';
import TrainingActivityReport from './TrainingActivityReport';
import TrainingMonitorReport from './TrainingMonitorReport';
import ServiceDelayWorkflowReport from './ServiceDelayWorkflowReport';
import AgeOutNextMonthBySCReport from './AgeOutNextMonthBySCReport';
import AgeOutByDistrictReport from './AgeOutByDistrictReport';
import TeamReviewReport from './TeamReviewReport';
import ExtendedServicesReport from './ExtendedServicesReport';
import TerminationCanonicalReport from './TerminationCanonicalReport';
import TravelVoucherHelperReport from './TravelVoucherHelperReport';
import CaseloadContactsReport from './CaseloadContactsReport';
import SchoolReferralNeededReport from './SchoolReferralNeededReport';
import SchoolLowIncidentDisabilityReport from './SchoolLowIncidentDisabilityReport';
import ClientDelayListReport from './ClientDelayListReport';
import ExtendedServicesReportPage from './ExtendedServicesReportPage';
import ContentHeader from '../components/ContentHeader';
import InfoIconButton from '../components/InfoIconButton';
import SideNavLayout from '../components/SideNavLayout';
import api, { reportsAPI } from '../services/api';
import { ReportsHubNavigationProvider } from '../contexts/ReportsHubNavigationContext';
import './ReportsHub.css';

function ReportsHub() {
  const { tenantCode, tenantPath } = useTenantFromUrl();
  const [searchParams, setSearchParams] = useSearchParams();
  const initializedRef = useRef(false);
  const [whoamiReady, setWhoamiReady] = useState(false);
  const [features, setFeatures] = useState({});
  const [permissions, setPermissions] = useState([]);

  const [selectedCategory, setSelectedCategory] = useState(REPORT_NAV_CATEGORIES[0]);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [reportParams, setReportParams] = useState({});
  const [showReportDescription, setShowReportDescription] = useState(false);
  const [expandedDescriptionCardId, setExpandedDescriptionCardId] = useState(null);
  const reportOwnHeaderIds = ['age-out-by-district', 'age-out-next-month-by-sc', 'team-review', 'termination-detailed', 'termination-summary', 'referral-source', 'transition-completed-on-time', 'referral-level-2-outcome', 'ct-due', 'client-ineligible', 'client-open-referrals', 'ifsp-plan-due', 'under-1', 'open-client-needs', 'providers-by-type-canonical', 'script-tasks-pending', 'tpc-access-vs-cornerstone', 'exit-access-vs-cornerstone', 'training-activity', 'training-monitor', 'service-delay-workflow', 'client-delay-list', 'caseload-tools', 'extended-services-report', 'extended-services-admin', 'extended-services-lea', 'extended-services-receiving', 'travel-voucher-helper', 'caseload-contacts', 'school-referral-needed', 'school-low-incident-disability', 'school-referral-completed'];

  useEffect(() => {
    initializedRef.current = false;
    setWhoamiReady(false);
    api
      .get('/whoami')
      .then((res) => {
        setFeatures(res.data?.features || {});
        setPermissions(Array.isArray(res.data?.permissions) ? res.data.permissions : []);
      })
      .catch(() => {
        setFeatures({});
        setPermissions([]);
      })
      .finally(() => {
        setWhoamiReady(true);
      });
  }, [tenantCode]);

  const reportsByCategory = useMemo(
    () => getReportsByCategory(tenantCode, features, permissions),
    [tenantCode, features, permissions]
  );
  /** Left rail: only groups with ≥1 report this user can open (hides empty categories). */
  const navCategories = useMemo(
    () => getReportNavCategoriesVisible(tenantCode, features, permissions),
    [tenantCode, features, permissions]
  );

  const hubInlineNavValue = useMemo(
    () => ({
      /** Return to the report picker for this hub; optional category keeps the left group in sync. */
      closeActiveReport: (category) => {
        setSelectedReportId(null);
        if (category && navCategories.includes(category)) {
          setSelectedCategory(category);
        } else if (navCategories.length > 0) {
          setSelectedCategory(navCategories[0]);
        }
      },
    }),
    [navCategories]
  );

  const reportsInSelectedCategory = useMemo(
    () => reportsByCategory.get(selectedCategory) || [],
    [reportsByCategory, selectedCategory]
  );

  const selectedReport = selectedReportId
    ? getReportById(selectedReportId, tenantCode, features, permissions)
    : null;
  const selectedReportInCategory =
    selectedReport && selectedReport.category === selectedCategory ? selectedReport : null;

  // Collapse description when switching to a different report
  useEffect(() => {
    setShowReportDescription(false);
  }, [selectedReportInCategory?.id]);

  // If the current group has no visible reports (e.g. permissions loaded), move to first non-empty group.
  useEffect(() => {
    if (selectedReportId) return;
    if (navCategories.length === 0) return;
    if (!navCategories.includes(selectedCategory)) {
      setSelectedCategory(navCategories[0]);
    }
  }, [navCategories, selectedCategory, selectedReportId]);

  // Initialize from URL after whoami so getReportById visibility matches permissions (avoids
  // remount e.g. browser Back from client: empty permissions made report "invisible", init
  // still marked done, and URL sync overwrote ?report= with default category).
  useEffect(() => {
    if (!whoamiReady) return;
    if (initializedRef.current) return;

    const reportIdFromUrl = searchParams.get('report');
    const categoryFromUrl = searchParams.get('category');

    if (reportIdFromUrl) {
      const report = getReportById(reportIdFromUrl, tenantCode, features, permissions);
      if (report) {
        setSelectedReportId(reportIdFromUrl);
        setSelectedCategory(report.category || REPORT_NAV_CATEGORIES[0]);
        initializedRef.current = true;
        return;
      }
    }

    if (categoryFromUrl && REPORT_NAV_CATEGORIES.includes(categoryFromUrl)) {
      setSelectedCategory(categoryFromUrl);
      setSelectedReportId(null);
    }
    initializedRef.current = true;
  }, [whoamiReady, searchParams, tenantCode, features, permissions]);

  // When category changes, clear report selection if it's not in the new category
  useEffect(() => {
    if (selectedReport && selectedReport.category !== selectedCategory) {
      setSelectedReportId(null);
    }
  }, [selectedCategory]);

  // Sync URL when report or category changes (after init).
  // Use replace: false when the report selection changes so browser Back returns to the Reports list/category instead of leaving Reports.
  useEffect(() => {
    if (!initializedRef.current) return;
    const next = new URLSearchParams(searchParams);
    if (selectedReportId) {
      next.set('report', selectedReportId);
      next.delete('category');
      // Persist report params (e.g. open date range) in URL
      if (selectedReportInCategory?.params) {
        selectedReportInCategory.params.forEach((param) => {
          const paramKey = param.id === 'openDateRange' ? 'od' : param.id;
          const pv = reportParams[param.id];
          if (pv) {
            next.set(`${paramKey}_preset`, pv.preset || 'all');
            if (pv.preset === 'custom') {
              if (pv.start) next.set(`${paramKey}_start`, pv.start);
              if (pv.end) next.set(`${paramKey}_end`, pv.end);
            }
          }
        });
      }
    } else {
      next.delete('report');
      if (selectedCategory) next.set('category', selectedCategory);
    }
    const nextStr = next.toString();
    if (nextStr !== searchParams.toString()) {
      const currentReport = searchParams.get('report');
      const nextReport = next.get('report');
      const reportSelectionChanged = (currentReport || '') !== (nextReport || '');
      setSearchParams(next, { replace: !reportSelectionChanged });
    }
  }, [selectedReportId, selectedCategory, selectedReportInCategory, reportParams, searchParams, setSearchParams]);

  // Report params (for reports that have params)
  useEffect(() => {
    if (!selectedReportId || !selectedReport?.params) {
      setReportParams({});
      return;
    }
    const report = getReportById(selectedReportId, tenantCode, features);
    if (!report?.params) return;
    const newParams = {};
    report.params.forEach((param) => {
      const paramKey = param.id === 'openDateRange' ? 'od' : param.id;
      const preset = searchParams.get(`${paramKey}_preset`) || param.default?.preset;
      const start = searchParams.get(`${paramKey}_start`) || '';
      const end = searchParams.get(`${paramKey}_end`) || '';
      newParams[param.id] = { preset, start, end };
    });
    setReportParams(newParams);
  }, [selectedReportId, searchParams, tenantCode, features]);

  const reportQuery = useMemo(() => {
    if (!selectedReportInCategory?.params) return {};
    const query = {};
    selectedReportInCategory.params.forEach((param) => {
      const paramValue = reportParams[param.id];
      if (!paramValue) return;
      if (param.type === 'dateRange') {
        if (paramValue.preset === 'last30') {
          const end = new Date();
          const start = new Date();
          start.setDate(start.getDate() - 30);
          query.open_date_from = start.toISOString().split('T')[0];
          query.open_date_to = end.toISOString().split('T')[0];
        } else if (paramValue.preset === 'custom') {
          if (paramValue.start) query.open_date_from = paramValue.start;
          if (paramValue.end) query.open_date_to = paramValue.end;
        }
      }
    });
    return query;
  }, [selectedReportInCategory, reportParams]);

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    // Clicking a category should always show the category card list.
    setSelectedReportId(null);
  };

  const handleReportSelect = (reportId) => {
    setSelectedReportId(reportId);
  };

  /** Export-only reports (no params, no detail view): click card = download CSV immediately. */
  const isExportOnly = (report) =>
    report?.kind === 'export' && report?.endpoints?.exportCsvUrl && !(report.params?.length);

  const handleCardClick = (report) => {
    if (isExportOnly(report)) {
      handleExportCsv(report);
      return;
    }
    handleReportSelect(report.id);
  };

  const handleParamChange = (paramId, field, value) => {
    setReportParams((prev) => ({
      ...prev,
      [paramId]: { ...prev[paramId], [field]: value },
    }));
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    setRefreshKey((k) => k + 1);
    setTimeout(() => setIsRefreshing(false), 500);
  };

  const handleExportCsv = async (report) => {
    if (!report?.endpoints?.exportCsvUrl) return;
    try {
      const response = await reportsAPI.exportCsv(report.endpoints.exportCsvUrl, reportQuery || {});
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = report.exportFilename || report.endpoints.exportCsvUrl?.replace('.csv', '_export.csv') || 'export.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error exporting CSV:', err);
      alert('Failed to export CSV');
    }
  };

  const renderParamFilters = () => {
    if (!selectedReportInCategory?.params?.length) return null;
    return (
      <div className="report-param-filters">
        {selectedReportInCategory.params.map((param) => {
          const paramValue = reportParams[param.id] || param.default;
          if (param.type === 'dateRange') {
            return (
              <div key={param.id} className="report-param-filter">
                <label className="report-param-label">{param.label}:</label>
                <select
                  value={paramValue?.preset || 'all'}
                  onChange={(e) => handleParamChange(param.id, 'preset', e.target.value)}
                  className="report-param-preset"
                >
                  {(param.presets || []).map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
                {paramValue?.preset === 'custom' && (
                  <>
                    <input
                      type="date"
                      value={paramValue.start || ''}
                      onChange={(e) => handleParamChange(param.id, 'start', e.target.value)}
                      className="report-param-date"
                      placeholder="Start"
                    />
                    <input
                      type="date"
                      value={paramValue.end || ''}
                      onChange={(e) => handleParamChange(param.id, 'end', e.target.value)}
                      className="report-param-date"
                      placeholder="End"
                    />
                  </>
                )}
              </div>
            );
          }
          return null;
        })}
      </div>
    );
  };

  const renderInlineReport = () => {
    if (!selectedReportInCategory || selectedReportInCategory.runMode !== 'inline') return null;
    switch (selectedReportInCategory.component) {
      case 'ClientStatusQuickEdit':
        return <ClientStatusQuickEdit embedded={true} refreshKey={refreshKey} query={reportQuery} />;
      case 'ClientsReport':
        return <ClientsReport embedded={true} refreshKey={refreshKey} query={reportQuery} />;
      case 'CaseloadToolsReport':
        return <CaseloadToolsReport embedded={true} refreshKey={refreshKey} tenantCode={tenantCode} />;
      case 'TerminationCanonicalReport':
        return (
          <TerminationCanonicalReport
            embedded={true}
            refreshKey={refreshKey}
            tenantCode={tenantCode}
            reportId={selectedReportInCategory.id}
          />
        );
      case 'TpcAccessVsCornerstoneReport':
        return <TpcAccessVsCornerstoneReport embedded={true} refreshKey={refreshKey} tenantCode={tenantCode} />;
      case 'ExitAccessVsCornerstoneReport':
        return <ExitAccessVsCornerstoneReport embedded={true} refreshKey={refreshKey} tenantCode={tenantCode} />;
      case 'TrainingActivityReport':
        return <TrainingActivityReport embedded={true} refreshKey={refreshKey} tenantCode={tenantCode} />;
      case 'TrainingMonitorReport':
        return <TrainingMonitorReport embedded={true} refreshKey={refreshKey} tenantCode={tenantCode} />;
      case 'ServiceDelayWorkflowReport':
        return <ServiceDelayWorkflowReport embedded={true} refreshKey={refreshKey} tenantCode={tenantCode} />;
      case 'AgeOutNextMonthBySCReport':
        return <AgeOutNextMonthBySCReport embedded={true} refreshKey={refreshKey} tenantCode={tenantCode} />;
      case 'AgeOutByDistrictReport':
        return <AgeOutByDistrictReport embedded={true} refreshKey={refreshKey} tenantCode={tenantCode} />;
      case 'TeamReviewReport':
        return <TeamReviewReport embedded={true} refreshKey={refreshKey} tenantCode={tenantCode} />;
      case 'ExtendedServicesReport':
        return (
          <ExtendedServicesReport
            embedded={true}
            refreshKey={refreshKey}
            query={reportQuery}
            report={selectedReportInCategory}
          />
        );
      case 'TravelVoucherHelperReport':
        return <TravelVoucherHelperReport embedded={true} refreshKey={refreshKey} tenantCode={tenantCode} />;
      case 'CaseloadContactsReport':
        return <CaseloadContactsReport embedded={true} refreshKey={refreshKey} tenantCode={tenantCode} />;
      case 'SchoolReferralNeededReport':
        return <SchoolReferralNeededReport embedded={true} refreshKey={refreshKey} tenantCode={tenantCode} />;
      case 'SchoolLowIncidentDisabilityReport':
        return <SchoolLowIncidentDisabilityReport embedded={true} refreshKey={refreshKey} tenantCode={tenantCode} />;
      case 'ClientDelayListReport':
        return <ClientDelayListReport embedded={true} refreshKey={refreshKey} tenantCode={tenantCode} />;
      case 'ExtendedServicesReportPage':
        return <ExtendedServicesReportPage embedded={true} refreshKey={refreshKey} tenantCode={tenantCode} />;
      default:
        return <div>Report component not found: {selectedReportInCategory.component}</div>;
    }
  };

  // Left nav: report categories (Monthly Contact moved to main left nav + Lists dropdown)
  const nav = (
    <>
      <div className="reports-nav-header side-nav-header">
        <h2 className="reports-nav-title side-nav-title">Report</h2>
        <p className="reports-nav-subtitle">Groups</p>
      </div>
      <div className="reports-list">
        {navCategories.length === 0 ? (
          <p className="reports-list-empty">No reports available for your account.</p>
        ) : (
          navCategories.map((category) => (
            <button
              key={category}
              type="button"
              className={`side-nav-link ${selectedCategory === category ? 'selected' : ''}`}
              onClick={() => handleCategorySelect(category)}
            >
              {category}
            </button>
          ))
        )}
      </div>
    </>
  );

  // Right panel: either list of reports in category or selected report detail
  const rightPanel = (
    <div className="reports-hub-right">
      {selectedReportInCategory ? (
        <>
          {selectedReportInCategory.kind === 'export' ? (
            <>
              <ContentHeader
                title={selectedReportInCategory.name}
                titleHint={selectedReportInCategory.description}
              />
              {selectedReportInCategory.endpoints?.exportCsvUrl && (
                <div className="report-export-actions">
                  <button
                    onClick={() => handleExportCsv(selectedReportInCategory)}
                    className="btn btn-outline"
                  >
                    Export CSV
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className={selectedReportInCategory?.id === 'caseload-tools' || selectedReportInCategory?.id === 'termination' ? 'report-detail report-detail--caseload-tools' : 'report-detail'}>
              {!reportOwnHeaderIds.includes(selectedReportInCategory.id) && (
                <div className="report-detail-header">
                  <h2 className="report-detail-title-with-info">
                    {selectedReportInCategory.name}
                    <InfoIconButton
                      onClick={() => setShowReportDescription((prev) => !prev)}
                      ariaExpanded={showReportDescription}
                      ariaControls="report-detail-description"
                      ariaLabel="Show description"
                      ariaLabelExpanded="Hide description"
                    />
                  </h2>
                  {showReportDescription && (
                    <p id="report-detail-description" className="report-detail-description">{selectedReportInCategory.description}</p>
                  )}
                </div>
              )}
              {renderParamFilters()}
              {((selectedReportInCategory.runMode === 'inline' &&
                selectedReportInCategory.id !== 'caseload-tools' &&
                selectedReportInCategory.id !== 'termination' &&
                !reportOwnHeaderIds.includes(selectedReportInCategory.id)) ||
                (selectedReportInCategory.route && selectedReportInCategory.runMode !== 'inline')) && (
                <div className="report-detail-actions">
                  {selectedReportInCategory.runMode === 'inline' && selectedReportInCategory.id !== 'caseload-tools' && selectedReportInCategory.id !== 'termination' && !reportOwnHeaderIds.includes(selectedReportInCategory.id) && (
                    <>
                      <button
                        onClick={handleRefresh}
                        className="btn btn-outline"
                        disabled={isRefreshing}
                      >
                        {isRefreshing ? 'Refreshing...' : 'Refresh'}
                      </button>
                      {selectedReportInCategory.endpoints?.exportCsvUrl && (
                        <button
                          onClick={() => handleExportCsv(selectedReportInCategory)}
                          className="btn btn-outline"
                        >
                          Export CSV
                        </button>
                      )}
                    </>
                  )}
                  {selectedReportInCategory.route && selectedReportInCategory.runMode !== 'inline' && (
                    <button
                      onClick={() => window.location.assign(tenantPath(selectedReportInCategory.route))}
                      className="btn btn-primary"
                    >
                      Open
                    </button>
                  )}
                </div>
              )}
              {selectedReportInCategory.runMode === 'inline' && (
                <ReportsHubNavigationProvider value={hubInlineNavValue}>
                  <div className="report-detail-content">{renderInlineReport()}</div>
                </ReportsHubNavigationProvider>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="reports-hub-picker">
          <ContentHeader
            title={selectedCategory}
            titleHint={`Choose a report or export below.`}
          />
          {reportsInSelectedCategory.length === 0 ? (
            <div className="reports-picker-empty">
              <p>No reports in this group yet.</p>
              <p className="reports-picker-empty-hint">Reports will appear in this group as they are added.</p>
            </div>
          ) : (
            <div className="reports-picker-grid">
              {reportsInSelectedCategory.map((report) => (
                <button
                  key={report.id}
                  type="button"
                  className="reports-picker-card"
                  onClick={() => handleCardClick(report)}
                >
                  <div className="reports-picker-card-header">
                    <span className="reports-picker-card-name">{report.name}</span>
                    {report.description && (
                      <InfoIconButton
                        className="reports-picker-card-info"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedDescriptionCardId((prev) => (prev === report.id ? null : report.id));
                        }}
                        ariaExpanded={expandedDescriptionCardId === report.id}
                        ariaControls={`report-card-desc-${report.id}`}
                        ariaLabel="Show description"
                        ariaLabelExpanded="Hide description"
                      />
                    )}
                  </div>
                  {report.description && expandedDescriptionCardId === report.id && (
                    <span id={`report-card-desc-${report.id}`} className="reports-picker-card-desc">
                      {report.description}
                    </span>
                  )}
                  {report.kind === 'export' && (
                    <span className="reports-picker-card-badge">Export</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="reports-hub">
      <SideNavLayout
        nav={nav}
        navClassName="reports-hub-nav"
        isolateCollapse
        collapseRail
        collapseRailExpandTitle="Show groups"
        collapseRailCollapseTitle="Hide groups"
        collapseRailExpandAria="Expand report groups panel"
        collapseRailCollapseAria="Collapse report groups panel"
      >
        {rightPanel}
      </SideNavLayout>
    </div>
  );
}

export default ReportsHub;
